<template>
    <el-dialog
        :visible.sync="dialogVisible"
        :close-on-click-modal="false"
        :close-on-press-escape="false"
        :show-close="currentStep === 0"
        :append-to-body="true"
        width="900px"
        top="5vh"
        custom-class="iva-wizard-dialog"
        @close="handleClose">
        <div slot="title" class="wizard-header">
            <h3 class="mb-2">Convert IVR to IVA</h3>
            <small class="text-muted">Transform your legacy IVR into a conversational AI assistant</small>
        </div>

        <div class="wizard-body">
            <!-- Steps indicator - Always visible -->
            <el-steps
                :active="currentStep"
                :align-center="true"
                finish-status="success"
                class="mb-2">
                <el-step title="IVR Analysis" icon="el-icon-document"></el-step>
                <el-step title="Mapping" icon="el-icon-connection"></el-step>
                <el-step title="Review & Apply" icon="el-icon-document-checked"></el-step>
                <el-step title="Finish" icon="el-icon-success"></el-step>
            </el-steps>

            <!-- AI-Style Loading for conversions and processing (only during actual loading operations) -->
            <div v-if="loading" class="ai-loading-state">
                <div class="ai-loading-content">
                    <div class="ai-pulse-container">
                        <div class="ai-pulse-ring"></div>
                        <div class="ai-pulse-ring"></div>
                        <div class="ai-pulse-ring"></div>
                        <i class="el-icon-cpu ai-cpu-icon"></i>
                    </div>
                    <h4 class="ai-loading-title">{{ loadingMessage }}</h4>
                    <p class="ai-loading-subtitle">{{ loadingSubtitle }}</p>
                    <div v-if="loadingDetails.length > 0" class="loading-details">
                        <div v-for="(detail, index) in loadingDetails" :key="index" class="detail-item">
                            <i class="el-icon-loading mr-2"></i>
                            {{ detail }}
                        </div>
                    </div>
                </div>
            </div>

            <!-- Step content -->
            <div v-show="!loading" class="wizard-content">
                <step1-source-analysis
                    v-show="currentStep === 0"
                    ref="step1"
                    :ivr-id="ivrId"
                    :wizard-data="wizardData"
                    @update="handleStep1Update"
                />

                <step2-intent-mapping
                    v-show="currentStep === 1"
                    ref="step2"
                    :ivr-id="ivrId"
                    :analysis-data="analysisData"
                    :wizard-data="wizardData"
                    @update="handleStep2Update"
                />

                <step4-review
                    v-show="currentStep === 2"
                    ref="step4"
                    :ivr-id="ivrId"
                    :wizard-data="wizardData"
                    :analysis-data="analysisData"
                    :conversion-result="conversionResult"
                    @update="handleStep4Update"
                />

                <step5-complete
                    v-show="currentStep === 3"
                    ref="step5"
                    :created-bot="createdBot"
                    :activated="wasActivated"
                    :line-name="lineName"
                    @done="handleComplete"
                />
            </div>
        </div>


        <div slot="footer" class="wizard-footer">
            <div class="footer-left">
                <el-button
                    v-if="currentStep < 3"
                    @click="handleClose"
                    :disabled="loading"
                    :type="currentStep === 2 ? 'danger' : 'default'"
                    :plain="currentStep !== 2">
                    {{ currentStep === 2 ? 'Discard' : 'Cancel' }}
                </el-button>
            </div>

            <div class="footer-right">
                <el-button
                    v-if="currentStep > 0 && currentStep < 3"
                    @click="previousStep"
                    :disabled="loading">
                    <i class="fa fa-arrow-left mr-1"></i>
                    Back
                </el-button>

                <el-button
                    v-if="currentStep < 2"
                    type="primary"
                    @click="nextStep"
                    :disabled="!canProceed || loading"
                    :loading="loading">
                    Next
                    <i class="fa fa-arrow-right ml-1"></i>
                </el-button>

                <template v-if="currentStep === 2">
                    <el-button
                        type="primary"
                        plain
                        @click="convertAndActivate(false)"
                        :disabled="loading"
                        :loading="loading">
                        <i class="fa fa-save mr-1"></i>
                        Create as a Draft
                    </el-button>

                    <el-button
                        type="success"
                        @click="acceptAndEnable"
                        :disabled="loading"
                        :loading="loading">
                        <i class="fa fa-check mr-1"></i>
                        Create & Activate
                    </el-button>
                </template>

                <el-button
                    v-if="currentStep === 3"
                    type="success"
                    @click="handleComplete">
                    <i class="el-icon-check"></i>
                    Done
                </el-button>
            </div>
        </div>
    </el-dialog>
</template>

<script>
import axios from 'axios'
import { mapState } from 'vuex'
import Step1SourceAnalysis from '../../components/iva-wizard/step-source-analysis.vue'
import Step2IntentMapping from '../../components/iva-wizard/step-intent-mapping.vue'
import Step4Review from '../../components/iva-wizard/step-review.vue'
import Step5Complete from '../../components/iva-wizard/step-complete.vue'

export default {
    name: 'ConvertIvrWizard',

    components: {
        Step1SourceAnalysis,
        Step2IntentMapping,
        Step4Review,
        Step5Complete,
    },

    props: {
        visible: {
            type: Boolean,
            default: false
        },
        ivrId: {
            type: Number,
            required: true
        },
        campaignId: {
            type: Number,
            default: null
        }
    },

    data() {
        return {
            dialogVisible: false,
            currentStep: 0,
            loading: false,
            loadingMessage: 'Processing...',
            loadingSubtitle: 'Please wait while we complete the process...',
            loadingDetails: [],

            analysisData: null,
            conversionResult: null,
            createdBot: null,
            wasActivated: false,
            lineName: '',
            lastParsedStep1Data: null,

            wizardData: {
                greeting: '',
                language: 'en',
                voice_id: '',
                voice_model: null,
                mapped_intents: [],
                target_inbox_tag: '',
                instructions: ''
            },
        }
    },

    computed: {
        ...mapState('cache', ['current_company']),

        canProceed() {
            switch (this.currentStep) {
                case 0:
                    return this.wizardData.greeting && this.wizardData.voice_id && this.wizardData.voice_model
                case 1:
                    return this.wizardData.mapped_intents.length > 0
                case 2:
                    return true
                default:
                    return false
            }
        }
    },

    watch: {
        visible(val) {
            this.dialogVisible = val
            if (val) {
                this.initializeWizard()
            }
        }
    },

    created() {
        this.dialogVisible = this.visible
    },

    async mounted() {
        if (this.visible) {
            this.initializeWizard()
        }
    },

    methods: {
        initializeWizard() {
            this.currentStep = 0
            this.analysisData = null
            this.conversionResult = null
            this.lastParsedStep1Data = null

            // Reset wizard data
            this.wizardData = {
                greeting: '',
                language: 'en',
                voice_id: '',
                voice_model: null,
                mapped_intents: [],
                target_inbox_tag: '',
                instructions: ''
            }
        },

        async nextStep() {
            // Stop any playing audio before advancing
            this.stopAllAudio()

            // Validate current step
            // Map step indices to ref names: 0->step1, 1->step2, 2->step4
            const stepRefMap = {
                0: 'step1',
                1: 'step2',
                2: 'step4'
            }
            const stepRef = stepRefMap[this.currentStep]
            if (stepRef && this.$refs[stepRef]?.validate) {
                const valid = await this.$refs[stepRef].validate()
                if (!valid) {
                    return
                }
            }

            // Perform step-specific actions
            if (this.currentStep === 0) {
                // After step 1, trigger intent parsing only if data changed or no cache exists
                const step1DataKey = JSON.stringify({
                    greeting: this.wizardData.greeting,
                    language: this.wizardData.language,
                    voice_id: this.wizardData.voice_id,
                    voice_model: this.wizardData.voice_model,
                    ivr_id: this.ivrId
                })

                // Only call API if data changed or no cached result exists
                const hasValidCache = this.conversionResult &&
                                     this.wizardData.mapped_intents &&
                                     this.wizardData.mapped_intents.length > 0 &&
                                     this.lastParsedStep1Data === step1DataKey

                if (!hasValidCache) {
                    await this.parseIntents()
                    this.lastParsedStep1Data = step1DataKey
                } else {
                    console.log('Using cached intent mapping data')
                }
            }

            this.currentStep++
        },

        previousStep() {
            // Stop any playing audio before going back
            this.stopAllAudio()

            if (this.currentStep > 0) {
                this.currentStep--
            }
        },

        handleStep1Update(data) {
            this.analysisData = data.analysisData
            Object.assign(this.wizardData, {
                greeting: data.greeting,
                language: data.language,
                voice_id: data.voice_id,
                voice_model: data.voice_model,
            })
        },

        handleStep2Update(data) {
            this.conversionResult = data.conversionResult
            this.wizardData.mapped_intents = data.mapped_intents
            if (data.ai_instructions) {
                this.wizardData.instructions = data.ai_instructions
            }
        },

        handleStep4Update(data) {
            // Final review updates if any
        },

        async parseIntents() {
            this.loading = true
            this.loadingMessage = 'Analyzing IVR Structure...'
            this.loadingSubtitle = 'Our AI is parsing your IVR configuration and mapping intents.'
            this.loadingDetails = [
                'Reading IVR menu structure',
                'Identifying call flow patterns',
                'Extracting key decision points',
                'Mapping intents to actions',
                'Generating conversational instructions'
            ]

            try {
                const response = await axios.post(`/api/v1/iva-wizard/parse-intents/${this.ivrId}`, {
                    analysis_data: this.analysisData
                })

                if (response.data.success) {
                    this.conversionResult = response.data.data.conversion_result
                    this.wizardData.mapped_intents = response.data.data.conversion_result.instructions
                    if (response.data.data.ai_instructions) {
                        this.wizardData.instructions = response.data.data.ai_instructions
                    }
                } else {
                    this.$message.error(response.data.message || 'Failed to parse intents')
                }
            } catch (error) {
                console.error('Parse Intents Error:', error)
                this.$message.error('Failed to parse intents: ' + (error.response?.data?.message || error.message))
            } finally {
                this.loading = false
                this.loadingDetails = []
            }
        },

        async acceptAndEnable() {
            // Validate step-review (step 2)
            if (this.$refs.step4 && !this.$refs.step4.validate()) {
                return
            }

            // Handle consent acceptance first (if needed)
            if (this.$refs.step4 && this.$refs.step4.handleConsentAcceptance) {
                try {
                    await this.$refs.step4.handleConsentAcceptance()
                } catch (error) {
                    // Error already handled in step-review
                    return
                }
            }

            // Proceed with conversion and activation
            await this.convertAndActivate(true)
        },

        async convertAndActivate(activate) {
            // Validate step-review (step 2)
            if (this.$refs.step4 && !this.$refs.step4.validate()) {
                return
            }

            this.loading = true
            this.loadingMessage = activate ? 'Creating and Activating IVA...' : 'Creating IVA Draft...'
            this.loadingSubtitle = activate
                ? 'Setting up your Intelligent Voice Assistant and activating it on your line.'
                : 'Saving your Intelligent Voice Assistant configuration as a draft.'

            if (activate) {
                this.loadingDetails = [
                    'Creating AloAi bot instance',
                    'Configuring voice and language settings',
                    'Setting up conversational intents',
                    'Compiling AI instructions',
                    'Activating on your line',
                    'Finalizing configuration'
                ]
            } else {
                this.loadingDetails = [
                    'Creating AloAi bot instance',
                    'Configuring voice and language settings',
                    'Setting up conversational intents',
                    'Saving draft configuration'
                ]
            }

            try {
                const payload = {
                    ...this.wizardData,
                    activate: activate,
                    campaign_id: this.campaignId
                }

                const response = await axios.post(
                    `/api/v1/iva-wizard/convert-and-activate/${this.ivrId}`,
                    payload
                )

                if (response.data.success) {
                    this.createdBot = response.data.data.bot
                    this.wasActivated = activate
                    this.lineName = response.data.data.line_name || 'Your Line'
                    this.currentStep = 3
                    this.$message.success(activate ? 'IVA created and activated!' : 'IVA draft created!')
                } else {
                    this.$message.error(response.data.message || 'Conversion failed')
                }
            } catch (error) {
                console.error('Convert & Activate Error:', error)
                this.$message.error('Conversion failed: ' + (error.response?.data?.message || error.message))
            } finally {
                this.loading = false
                this.loadingDetails = []
            }
        },

        handleComplete() {
            this.$emit('conversion-complete', { bot: this.createdBot, activated: this.wasActivated })
            this.handleClose()
        },

        stopAllAudio() {
            // Stop all playing audio elements within this dialog
            const audioElements = this.$el.querySelectorAll('audio')
            audioElements.forEach(audio => {
                audio.pause()
                audio.currentTime = 0
            })

            // Stop voice preview audio in step1 if active
            if (this.$refs.step1 && this.$refs.step1.stopAllAudio) {
                this.$refs.step1.stopAllAudio()
            }
        },

        handleClose() {
            if (this.loading) {
                return
            }

            this.stopAllAudio()

            this.$emit('close')
            this.dialogVisible = false
        }
    }
}
</script>

<style>
.iva-wizard-dialog .el-dialog__body {
    padding: 15px 30px 0 30px;
    min-height: 400px;
}
</style>

<style scoped>
.wizard-header h3 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
}

.wizard-body {
    min-height: 450px;
    padding-bottom: 0;
    margin-bottom: 0;
}

.wizard-content {
    margin-top: 15px;
    margin-bottom: 0;
}

.wizard-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 10px;
    border-top: 1px solid #eee;
}

.footer-left {
    flex: 0 0 auto;
}

.footer-right {
    flex: 0 0 auto;
    display: flex;
    gap: 10px;
}

/* AI-Style Loading */
.ai-loading-state {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 500px;
    padding: 20px 20px 20px;
}

.ai-loading-content {
    text-align: center;
    max-width: 500px;
}

.ai-pulse-container {
    position: relative;
    width: 140px;
    height: 140px;
    margin: 0 auto 30px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.ai-cpu-icon {
    font-size: 56px;
    color: #409eff;
    z-index: 10;
    position: relative;
}

.ai-pulse-ring {
    position: absolute;
    width: 100%;
    height: 100%;
    border: 3px solid #409eff;
    border-radius: 50%;
    opacity: 0;
    animation: ai-pulse 2s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}

.ai-pulse-ring:nth-child(1) {
    animation-delay: 0s;
}

.ai-pulse-ring:nth-child(2) {
    animation-delay: 0.5s;
}

.ai-pulse-ring:nth-child(3) {
    animation-delay: 1s;
}

@keyframes ai-pulse {
    0% {
        transform: scale(0.5);
        opacity: 0;
    }
    50% {
        opacity: 0.4;
    }
    100% {
        transform: scale(1.3);
        opacity: 0;
    }
}

.ai-loading-title {
    font-size: 22px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 8px;
    background: linear-gradient(90deg, #409eff 0%, #67c23a 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.ai-loading-subtitle {
    font-size: 15px;
    color: #909399;
    margin: 0 0 8px 0;
    word-break: keep-all;
    word-wrap: break-word;
    overflow-wrap: break-word;
}

.loading-details {
    max-width: 400px;
    margin: 5px auto 0;
}

.detail-item {
    text-align: left;
    padding: 6px 12px;
    margin: 3px 0;
    background: #f5f7fa;
    border-radius: 4px;
    font-size: 13px;
    color: #606266;
    display: flex;
    align-items: center;
}

.detail-item i {
    color: #409eff;
}
</style>
